import { Divider, Skeleton, Text } from '@chakra-ui/react';
import { withLDConsumer } from 'launchdarkly-react-client-sdk';
import React, { useEffect, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';

import { utils as commonUtils } from '@finley-technologies/common';

import { useQueryParams } from 'packages/common-frontend/hooks/useQueryParams';
import { useGetCreditFacilityQuery } from 'packages/credit-facility/requests/requests.gql.generated';
import { creditFacilityIdQueryParam } from 'packages/credit-facility/ui/CreditFacilitySelect';
import { CollapsableSideBar } from 'packages/sidebar/ui/CollapsableSideBar';
import { SideBarHeader } from 'packages/sidebar/ui/SideBarHeader';
import { SideBarItem } from 'packages/sidebar/ui/SideBarItem';
import {
  getSideBarModeFromPath,
  SideBarMode,
} from 'packages/sidebar/ui/sideBarUtils';

const isNotProduction = !commonUtils.EnvUtils.isProduction();

export const CapitalProviderSideBarRoot: React.FC<{
  setSideBarMode: React.Dispatch<React.SetStateAction<SideBarMode>>;
}> = withLDConsumer()(({ flags, setSideBarMode }) => {
  const { pathname: currentPath } = useLocation();

  return (
    <CollapsableSideBar>
      <SideBarItem
        currentPath={currentPath}
        label="Portfolio"
        routeName="portfolio"
        icon="house"
      />
      {isNotProduction && (
        <SideBarItem
          currentPath={currentPath}
          label="Credit Agreements"
          routeName="credit-agreements"
          icon="file-lines"
        />
      )}
      <SideBarItem
        currentPath={currentPath}
        label="Funding Requests"
        routeName="funding-requests"
        icon="dollar-sign"
      />
      <SideBarItem
        routeName="deliverables"
        currentPath={currentPath}
        label="Deliverables"
        icon="calendar"
      />
      <SideBarItem
        routeName="reports"
        currentPath={currentPath}
        label="Reports"
        icon="folder-arrow-down"
      />

      <Divider style={{ marginTop: '24px', marginBottom: '24px' }} />

      <SideBarItem
        routeName="calculation-terminologies"
        currentPath={currentPath}
        label="Calculation Terminologies"
        icon="book"
      />
      {/* Too hard to filter events - just hide the entire section if we're filtering facilities. */}
      {!flags?.creditFacilitySlugsToHide?.length && (
        <SideBarItem
          routeName="events"
          currentPath={currentPath}
          label="Events Log"
          icon="clock"
        />
      )}
      <SideBarItem
        routeName="settings"
        currentPath={currentPath}
        label="Settings"
        icon="gear"
        onClick={() => setSideBarMode(SideBarMode.settings)}
      />
    </CollapsableSideBar>
  );
});

const CapitalProviderSideBarSettingsFacilityHeader: React.FC<{
  creditFacilityId: string;
}> = ({ creditFacilityId }) => {
  const {
    data: creditFacilityData,
    loading: isCreditFacilityLoading,
  } = useGetCreditFacilityQuery({ variables: { creditFacilityId } });

  if (!creditFacilityData || isCreditFacilityLoading) {
    return (
      <Skeleton
        h="600px"
        data-testid="settings-facility-sidebar-header-loading"
      />
    );
  }

  const creditFacilityName = commonUtils.creditFacilitySlugToName(
    creditFacilityData.creditFacility.slug,
  );

  return (
    <Text fontSize="14px" pl="20px" pb={4} fontWeight="semibold">
      {creditFacilityName}
    </Text>
  );
};

export const CapitalProviderSideBarSettings = withLDConsumer()(() => {
  const { pathname: currentPath } = useLocation();
  const history = useHistory();

  const query = useQueryParams();
  const creditFacilityId = query.get(creditFacilityIdQueryParam);

  return (
    <CollapsableSideBar
      dividerStyleProps={{ marginTop: '24px', marginBottom: '0px' }}
    >
      <SideBarHeader
        headerLabel="Exit Settings"
        onClickPrevious={() => {
          history.push('/');
        }}
      />

      <Divider style={{ marginTop: '6px', marginBottom: '12px' }} />

      <SideBarItem
        routeName="settings/my-account"
        currentPath={currentPath}
        label="My Account"
        icon="user"
        textViewMarginLeft="20px"
      />
      <SideBarItem
        currentPath={currentPath}
        label="Facilities"
        routeName="settings/facilities"
        icon="building-columns"
        strictPathMatch
        textViewMarginLeft="20px"
      />

      {/* show facility level settings if cf is selected */}
      {creditFacilityId && (
        <Divider style={{ marginTop: '12px', marginBottom: '12px' }} />
      )}
      {creditFacilityId && (
        <CapitalProviderSideBarSettingsFacilityHeader
          creditFacilityId={creditFacilityId}
        />
      )}

      {creditFacilityId && (
        <SideBarItem
          routeName="settings/facilities/borrowing-base/"
          // attach the credit facility query string to each side bar item
          routeAddress={`settings/facilities/borrowing-base/configuration${`?${creditFacilityIdQueryParam}=${creditFacilityId}`}`}
          currentPath={currentPath}
          label="Borrowing Base"
          icon="money-check-dollar"
          textViewMarginLeft="20px"
        />
      )}
    </CollapsableSideBar>
  );
});

export const getSideBarForMode = ({
  sideBarMode,
  setSideBarMode,
}: {
  sideBarMode: SideBarMode;
  setSideBarMode: React.Dispatch<React.SetStateAction<SideBarMode>>;
}): React.ReactElement => {
  switch (sideBarMode) {
    case SideBarMode.root:
      return <CapitalProviderSideBarRoot setSideBarMode={setSideBarMode} />;
    case SideBarMode.settings:
    case SideBarMode.settingsFacility:
      return <CapitalProviderSideBarSettings />;
    default:
      return <CapitalProviderSideBarRoot setSideBarMode={setSideBarMode} />;
  }
};

export const CapitalProviderSideBar = withLDConsumer()(({ flags }) => {
  const { pathname: currentPath } = useLocation();

  const [sideBarMode, setSideBarMode] = useState<SideBarMode>(SideBarMode.root);

  useEffect(() => {
    const currrentSideBarMode = getSideBarModeFromPath({ currentPath });
    setSideBarMode(currrentSideBarMode);
  }, [currentPath]);

  return flags?.displayLenderSettingsFacilities ? (
    getSideBarForMode({ sideBarMode, setSideBarMode })
  ) : (
    <CapitalProviderSideBarRoot setSideBarMode={setSideBarMode} />
  );
});
